/**
 * CSV export of whatever the current view is showing.
 *
 * It exports the rows on screen, not the whole result set. That is the honest
 * scope for a button sitting next to a paginated table — silently exporting
 * 4,000 rows when the user can see 25 would be a surprise, and re-querying
 * every page behind a click would be worse.
 */

const EXCEL_FORMULA_START = /^[=+\-@\t\r]/;

/**
 * Quote a single field.
 *
 * The leading apostrophe on formula-like values is deliberate: a cell opening
 * with =, +, - or @ is executed as a formula by Excel and Sheets, so an
 * extracted value could run as code in the reviewer's spreadsheet. Prefixing
 * makes it inert text.
 */
function cell(v: unknown): string {
    if (v === null || v === undefined) return "";

    let s: string;
    if (Array.isArray(v)) s = v.map((x) => String(x)).join("; ");
    else if (typeof v === "object") s = JSON.stringify(v);
    else s = String(v);

    if (EXCEL_FORMULA_START.test(s)) s = `'${s}`;

    return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export interface Column<T> {
    header: string;
    value: (row: T) => unknown;
}

export function toCsv<T>(rows: T[], columns: Column<T>[]): string {
    const head = columns.map((c) => cell(c.header)).join(",");
    const body = rows.map((r) => columns.map((c) => cell(c.value(r))).join(","));
    return [head, ...body].join("\r\n");
}

/** Trigger a download of `text` as `filename`. */
export function downloadCsv(filename: string, text: string): void {
    // A BOM, so Excel opens UTF-8 correctly. Without it the Arabic in these
    // documents renders as mojibake on a default Windows install.
    const blob = new Blob(["﻿", text], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();

    // Freed on the next tick rather than immediately: revoking synchronously
    // can cancel the download in some browsers before it starts.
    setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** `documents-2026-09-08.csv` */
export const stampedName = (base: string): string =>
    `${base}-${new Date().toISOString().slice(0, 10)}.csv`;
